import { setBlockType } from 'prosemirror-commands';
import { keymap } from 'prosemirror-keymap';
import type { NodeType, Schema } from 'prosemirror-model';
import { NodeRange } from 'prosemirror-model';
import type { EditorState, NodeSelection, Transaction } from 'prosemirror-state';
import { TextSelection } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';

import type { HeadingAttrs, HeadingSchema } from './heading-node';
import type { HeadingMenuController } from './heading-plugin.types';
import type { EditorButton, EditorMode, RtPMPlugin } from '../../editor/types';
import { isNodeOfTypeName, isNodeType } from '../../helpers/node-helpers';
import { DEFAULT_KEYMAP_INPUTRULE_PRIORITY } from '../../helpers/utils';

export class HeadingService {
  private isParagraph = isNodeType(this.schema.nodes.paragraph);
  private isAnyHeading = isNodeOfTypeName(this.schema.nodes.heading.name);

  constructor(
    protected nodeType: NodeType,
    protected schema: Schema,
    protected menuController: HeadingMenuController,
  ) {}

  public isActive(_: EditorState) {
    return this.menuController.isOpen();
  }

  public getCurrentLevel(state: EditorState): HeadingAttrs {
    if (this.isAnyHeading(state)) {
      const { $from, node } = <NodeSelection>state.selection;
      if (node) return <HeadingAttrs>node.attrs;
      return <HeadingAttrs>$from.parent.attrs;
    }
    return { level: 0 };
  }

  public setHeading(state: EditorState, attrs: HeadingAttrs, dispatch?: (tr: Transaction) => void) {
    if (this.isHeadingOfLevel(attrs)(state)) {
      return false;
    }
    const { selection, schema } = state;
    if (selection.$anchor.depth === 1) {
      const cmd = setBlockType(schema.nodes.heading, attrs);
      return cmd(state, dispatch);
    } else {
      const tr = this.liftAndSetHeading(state, attrs);
      if (!tr) return false;
      dispatch && dispatch(tr);
      return true;
    }
  }

  public setParagraph(state: EditorState, dispatch?: (tr: Transaction) => void) {
    if (!this.isParagraph(state)) {
      const cmd = setBlockType(state.schema.nodes.paragraph);
      return cmd(state, dispatch);
    }
    return false;
  }

  public trigger(
    state: EditorState,
    dispatch: (tr: Transaction) => void,
    origin: HTMLElement,
    isContextMenu: boolean,
    afterSelected?: () => void,
  ) {
    const onSelect = (val: HeadingAttrs) => {
      if (val.level) {
        this.setHeading(state, val, dispatch);
      } else {
        this.setParagraph(state, dispatch);
      }
      this.menuController.close();
      afterSelected && afterSelected();
    };
    const { backdropClick$ } = this.menuController.open(origin.getBoundingClientRect(), {
      onSelect,
      initialLevel: this.getCurrentLevel(state).level,
    });

    backdropClick$.subscribe(() => this.menuController.close());
  }

  private isHeadingOfLevel = (attrs: HeadingAttrs) => isNodeType(this.schema.nodes.heading, attrs);

  private liftAndSetHeading(state: EditorState, attrs: HeadingAttrs): Transaction | false {
    const { selection, tr, doc, schema } = state;
    const $from = doc.resolve(selection.from);

    // Check if the selection is inside a columns node.
    let inColumns = false;
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === 'columns') {
        inColumns = true;
        break;
      }
    }

    // If inside columns, change block type (without lifting).
    if (inColumns) {
      return tr.setBlockType(selection.from, selection.to, schema.nodes.heading, attrs);
    }

    if (!(selection instanceof TextSelection)) return false;
    const paragraphs: Array<{ start: number; end: number; depth: number }> = [];
    doc.nodesBetween(selection.from, selection.to, (node, pos) => {
      if (node.type === schema.nodes.paragraph) {
        const pos$ = doc.resolve(pos);
        const depth = pos$.depth;
        paragraphs.push({
          start: pos$.start(),
          end: pos$.end(),
          depth,
        });
      }
    });
    for (let i = paragraphs.length - 1; i >= 0; i--) {
      const range = new NodeRange(
        tr.doc.resolve(paragraphs[i].start),
        tr.doc.resolve(paragraphs[i].end),
        paragraphs[i].depth,
      );
      tr.lift(range, 0);
      tr.setBlockType(paragraphs[i].start + 1, undefined, schema.nodes.heading, attrs);
    }
    return tr;
  }
}

export class HeadingPlugin implements RtPMPlugin {
  public api: {
    headingService: HeadingService;
  };

  public buttons = {
    toggleHeading: <EditorButton>{
      isDropdown: true,
      labelTranslationKey: state => {
        const { level } = this.api.headingService.getCurrentLevel(state);
        return level ? `wysiwyg.heading${level}` : 'wysiwyg.text';
      },
      tooltipText: 'wysiwyg.styles',
      isActive: state => this.api.headingService.isActive(state),
      run: (...args) => {
        this.api.headingService.trigger(...args, () => this.editorView.focus());
      },
    },
  };

  private editorView: EditorView;

  constructor(protected menuController: HeadingMenuController) {}

  public beforeInit(mode: EditorMode, schema: HeadingSchema) {
    this.api = {
      headingService: new HeadingService(schema.nodes.heading, schema, this.menuController),
    };
  }

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;
  }

  public getNativeEditorPlugins(schema: HeadingSchema) {
    return [
      { priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY, plugin: keymap(this.getKeyBindings(schema)) },
    ];
  }

  private getKeyBindings(schema: HeadingSchema) {
    return {};
  }
}
